import { useLayoutEffect } from 'react';
import { create } from 'zustand';

interface DocumentTitleOverrideState {
    /** A title that outranks the route's, set by a page that is not what the route names. */
    title: string | null;
}

const useOverrideStore = create<DocumentTitleOverrideState>(() => ({ title: null }));

/** The title a mounted page asked for in place of the route's, or null. */
export function useDocumentTitleOverrideValue(): string | null {
    return useOverrideStore((state) => state.title);
}

/**
 * Names the browser tab `title` while the caller is mounted, over the route's own title,
 * and hands the route's title back on unmount. For a state that replaces the page the route
 * names, such as "Page not found" under a site that does not exist. It also sets the title
 * itself, for the states that render outside the app shell.
 */
export function useDocumentTitleOverride(title: string): void {
    // A layout effect, so the tab never paints the route's title for a page that is not there.
    useLayoutEffect(() => {
        useOverrideStore.setState({ title });
        document.title = title;
        return () => {
            if (useOverrideStore.getState().title === title) {
                useOverrideStore.setState({ title: null });
            }
        };
    }, [title]);
}
